import {
  Action,
  Actions,
  FlagProps,
  Flags,
  IconButton,
  Parameter,
  Parameters,
} from 'platform/components';
import {Clickable, Heading, HStack, Icon, Show, ThemeIconKey, VStack} from 'platform/foundation';

import {HTMLAttributes, MouseEvent, ReactNode, useId} from 'react';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {DragHandleSlot} from './DragHandleSlot';

interface TemplateCardHeaderProps extends TestIdProps {
  title: string;
  titleIcon: ThemeIconKey;
  flags: FlagProps[];
  parameters?: Parameter[];
  actions?: Action[];
  handleProps?: HTMLAttributes<HTMLDivElement>;
  /**
   * Reserves the drag-handle slot even when `handleProps` is absent (no handle rendered), so the
   * header content does not shift while the handle is temporarily hidden (e.g. during a pending
   * edit) and non-sortable cards in the same list share the title left edge.
   */
  isReorderingAvailable: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  children?: ReactNode;
}

const stopPropagation = (event: MouseEvent<HTMLDivElement>) => event.stopPropagation();

/**
 * Replicates the shared Card header (title row, flags, actions, expand button, parameters) inside
 * a header-less Card so the drag handle can sit in the header's leading position. The shared Card
 * has no header slot for custom leading content.
 */
export function TemplateCardHeader(props: TemplateCardHeaderProps) {
  const bodyId = useId();
  const [isHeaderHovered, onHeaderMouseEnter, onHeaderMouseLeave] = useBoolean();
  const hasActions = isNotNil(props.actions) && props.actions.length > 0;

  return (
    <VStack spacing={4}>
      <Clickable
        onClick={props.onToggle}
        onMouseEnter={onHeaderMouseEnter}
        onMouseLeave={onHeaderMouseLeave}
      >
        <HStack spacing={3}>
          <Show when={props.isReorderingAvailable}>
            <VStack height={hasActions ? 8 : 6} justify="center">
              <div onClick={stopPropagation}>
                <DragHandleSlot handleProps={props.handleProps} />
              </div>
            </VStack>
          </Show>
          <VStack spacing={1} width="100%">
            <HStack justify="space-between" wrap>
              <HStack spacing={2} align="center">
                <Icon
                  value={props.titleIcon}
                  color="text.primary"
                  size={4}
                  data-testid={suffixTestId('cardHeadingTitleIcon', props)}
                />
                <Heading size={4} data-testid={suffixTestId('cardHeadingTitle', props)}>
                  {props.title}
                </Heading>
                <Flags
                  flags={props.flags}
                  spacing={2}
                  data-testid={suffixTestId('cardHeadingFlags', props)}
                />
              </HStack>
              <HStack spacing={2} align="center">
                <Show when={hasActions}>
                  <div onClick={stopPropagation}>
                    <Actions
                      actions={props.actions}
                      size="default"
                      data-testid={suffixTestId('cardHeaderActions', props)}
                    />
                  </div>
                </Show>
                <IconButton
                  size="small"
                  priority={isHeaderHovered ? 'secondary' : 'default'}
                  icon={props.isExpanded ? 'navigation/expand_less' : 'navigation/expand_more'}
                  aria-label={props.title}
                  aria-controls={bodyId}
                  aria-expanded={props.isExpanded}
                  data-testid={suffixTestId('cardHeaderExpandButton', props)}
                />
              </HStack>
            </HStack>
            <Show when={props.parameters}>
              <HStack>
                <Parameters
                  parameters={props.parameters}
                  size="small"
                  color="secondary"
                  data-testid={suffixTestId('cardHeadingParameters', props)}
                />
              </HStack>
            </Show>
          </VStack>
        </HStack>
      </Clickable>
      <Show when={props.isExpanded}>
        <div id={bodyId} data-testid={suffixTestId('cardBody', props)}>
          {props.children}
        </div>
      </Show>
    </VStack>
  );
}
